import { useState } from "react";
import { useNavigate } from "react-router-dom";
import profileApi from "api/profile";
import useNotify from "hooks/useNotify";
import { useAppContext } from "hooks/useContext";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "store/actions";
import {
  PREFERRED_CURRENCY_USD_ID,
  SHOW_VENDOR_PREFERRED_CURRENCY_UI,
} from "utils/constantValues";

function useVendorForm({ vendorId, fullName }) {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAppContext();
  const userId = user?.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.userDetails || {});

  const { notifyError, notifySuccess } = useNotify();

  const defaultsValues = {
    //bankDetails
    vatRegistrationNumber: "",
    vatCertification: "",
    isVatExempt: false,
    vatCountryId: "",
    preferredCurrency: SHOW_VENDOR_PREFERRED_CURRENCY_UI
      ? userDetails?.currency?.id ?? PREFERRED_CURRENCY_USD_ID
      : PREFERRED_CURRENCY_USD_ID,
    accountName: "",
    bankName: "",
    accountType: "",
    accountNumber: "",
    swiftCode: "",
    sortCode: "",
    iban: "",
    ureed: "",
    payoneer: "",
    fullName: "",
    cashExpressPhoneNumber: "",

    //contacts
    permanentAddress: "",
    currentResidenceAddress: "",
    country: "",
    phoneNumber: "",
    homeTelephone: "",

    //emergencyContacts
    name: "",
    relationship: "",
    email: "",
    emergencyCountry: "",
    emergencyHomeTelephone: "",
    emergencyPhoneNumber: "",

    //personalDetails
    gender: "",
    dateOfBirth: "",
    countryOfBirth: "",
    martialStatus: "",
    nationalCardNo: "",
    socialSecurityNo: "",
    passportNo: "",
    expiryDate: "",
    placeOfIssue: "",
    countryOfIssue: "",

    //qualifications
    university: "",
    qualifications: "",
    major: "",
  };
  const onSubmit = async ({
    //bankDetails
    vatRegistrationNumber = "",
    vatCertification = "",
    isVatExempt = false,
    vatCountryId = "",
    preferredCurrency,
    accountName,
    bankName,
    accountType,
    accountNumber,
    swiftCode,
    sortCode,
    iban,
    ureed,
    payoneer,
    fullName,
    cashExpressPhoneNumber,

    permanentAddress,
    currentResidenceAddress,
    country,
    phoneNumber,
    homeTelephone,
    name,
    relationship,
    email,
    emergencyCountry,
    emergencyPhoneNumber,
    emergencyHomeTelephone,

    gender,
    dateOfBirth,
    countryOfBirth,
    martialStatus,
    nationalCardNo,
    socialSecurityNo,
    passportNo,
    expiryDate,
    placeOfIssue,
    countryOfIssue,

    university,
    qualification,
    major,
  }) => {
    setIsLoading(true);
    const bankDetails = {
      accountName,
      bankName,
      accountType,
      accountNumber,
      swiftCode,
      sortCode,
      iban,
    };

    const ureedDetails = {
      ureedEmail: ureed,
    };
    const payoneerDetails = {
      payoneerEmail: payoneer,
    };

    const cashExpress = {
      fullName,
      cashExpressPhoneNumber: cashExpressPhoneNumber,
    };

    const contacts = {
      permanentAddress,
      currentResidenceAddress,
      country,
      phoneNumber,
      homeTelephone,
    };

    const emergencyContacts = {
      name,
      relationship,
      email,
      country: emergencyCountry,
      mobileNumber: emergencyPhoneNumber,
      homeTelephone: emergencyHomeTelephone,
    };

    const personalDetails = {
      gender,
      dateOfBirth,
      countryOfBirth,
      martialStatus,
      nationalCardNo,
      socialSecurityNo,
      passportNo,
      expiryDate,
      placeOfIssue,
      countryOfIssue,
    };

    const qualifications = {
      university,
      qualification,
      major,
    };

    const data = {
      preferredCurrency: SHOW_VENDOR_PREFERRED_CURRENCY_UI
        ? preferredCurrency || PREFERRED_CURRENCY_USD_ID
        : PREFERRED_CURRENCY_USD_ID,
      bankDetails,
      ureedDetails,
      payoneerDetails,
      cashExpress,
      contacts,
      emergencyContacts,
      personalDetails,
      qualifications,
      vatRegistrationNumber,
      vatCertification,
      isVatExempt,
      vatCountry: vatCountryId,
    };

    try {
      await profileApi.updateAdditionalVendorInfo(vendorId, data);
      notifySuccess("Has been updated successfully");
      const userData = await profileApi.getUserProfileByIdApi(userId);
      setTimeout(() => {
        dispatch(actions.setUserDetails(userData));
        window.scrollTo(0, 0);
        navigate("/");
      }, 400);
    } catch (err) {
      console.error(err);
      notifyError(err?.data?.error ?? err?.message ?? "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };
  return { onSubmit, isLoading, defaultsValues };
}

export default useVendorForm;
